import React, { useRef, useEffect, useState, useMemo } from "react";
import BackgroundAnimations from "./overview/BackgroundAnimations";
import StatsSection from "./overview/StatsSection";
import FeatureDisplay from "./overview/FeatureDisplay";
import NavigationDots from "./overview/NavigationDots";
import ScrollInstruction from "./overview/ScrollInstruction";
import { getFeaturesData } from "./overview/featuresData";
import { useLanguage } from "@/context/LanguageContext";
import { commonLabels } from "@/i18n";

const OverviewSection = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { language } = useLanguage();
  const [currentSection, setCurrentSection] = useState(0);
  const [isScrollLocked, setIsScrollLocked] = useState(false);
  const [hasCompletedAllSections, setHasCompletedAllSections] = useState(false);

  const labels = useMemo(() => commonLabels(language), [language]);
  const overview = labels.menuPages?.howItWorks?.overviewSection || {};
  const sectionTitle = overview.title ?? "BILENIA i ett Nötskål";
  const sectionDescription = overview.description ?? "";
  const loadingText = overview.loading ?? "Laddar innehåll...";

  // Dynamically get features data based on current language
  const featuresData = useMemo(() => getFeaturesData(language), [language]);

  // Reset currentSection when featuresData changes (e.g., language change)
  useEffect(() => {
    if (featuresData.length > 0 && currentSection >= featuresData.length) {
      setCurrentSection(0);
    }
  }, [featuresData.length, currentSection]);

  // Add bounds checking function
  const updateCurrentSection = (newSection: number) => {
    if (featuresData.length === 0) return;
    const boundedSection = Math.max(
      0,
      Math.min(newSection, featuresData.length - 1),
    );
    setCurrentSection(boundedSection);
  };

  useEffect(() => {
    // Don't set up scroll handlers if no features data
    if (!featuresData || featuresData.length === 0) return;

    const handleWheel = (e: WheelEvent) => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      // Only capture scroll when section is dominant in viewport (not when it's just peeking in).
      // Section must be at least ~60% in view: top above 30% from top, bottom below 70% from top.
      const threshold = 0.3 * window.innerHeight;
      const isSectionDominant =
        rect.top <= threshold && rect.bottom >= window.innerHeight - threshold;

      if (isSectionDominant) {
        // Always handle scroll events when in section, regardless of completion status
        if (!isScrollLocked && featuresData.length > 0) {
          if (e.deltaY > 0 && currentSection < featuresData.length - 1) {
            // Scroll down to next section
            e.preventDefault();
            e.stopPropagation();
            updateCurrentSection(currentSection + 1);
            setIsScrollLocked(true);
            setTimeout(() => setIsScrollLocked(false), 800);
          } else if (e.deltaY < 0 && currentSection > 0) {
            // Scroll up to previous section
            e.preventDefault();
            e.stopPropagation();
            updateCurrentSection(currentSection - 1);
            setIsScrollLocked(true);
            setTimeout(() => setIsScrollLocked(false), 800);
          } else if (
            e.deltaY > 0 &&
            currentSection === featuresData.length - 1 &&
            !hasCompletedAllSections
          ) {
            // Reached the last section, allow normal scrolling
            e.preventDefault();
            e.stopPropagation();
            setHasCompletedAllSections(true);
            setIsScrollLocked(true);
            setTimeout(() => {
              setIsScrollLocked(false);
              // Smooth scroll to the next section below
              const nextElement = containerRef.current?.nextElementSibling;
              if (nextElement) {
                nextElement.scrollIntoView({ behavior: "smooth" });
              }
            }, 300);
          } else if (
            e.deltaY < 0 &&
            currentSection === 0 &&
            hasCompletedAllSections
          ) {
            // At first section and trying to scroll up, allow normal scrolling up
            e.preventDefault();
            e.stopPropagation();
            setHasCompletedAllSections(false);
            setIsScrollLocked(true);
            setTimeout(() => {
              setIsScrollLocked(false);
              // Smooth scroll to the previous section above
              const prevElement = containerRef.current?.previousElementSibling;
              if (prevElement) {
                prevElement.scrollIntoView({ behavior: "smooth" });
              }
            }, 300);
          }
        } else if (!isScrollLocked) {
          // If scroll is locked, prevent default to avoid janky scrolling
          if (
            currentSection < featuresData.length - 1 ||
            !hasCompletedAllSections
          ) {
            e.preventDefault();
            e.stopPropagation();
          }
        }
      }
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const threshold = 0.3 * window.innerHeight;
      const isSectionDominant =
        rect.top <= threshold && rect.bottom >= window.innerHeight - threshold;

      if (isSectionDominant) {
        e.preventDefault();
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const threshold = 0.3 * window.innerHeight;
      const isSectionDominant =
        rect.top <= threshold && rect.bottom >= window.innerHeight - threshold;

      if (isSectionDominant && !isScrollLocked) {
        e.preventDefault();
      }
    };

    document.addEventListener("wheel", handleWheel, {
      passive: false,
      capture: true,
    });
    document.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    document.addEventListener("touchmove", handleTouchMove, { passive: false });

    return () => {
      document.removeEventListener("wheel", handleWheel);
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
    };
  }, [currentSection, isScrollLocked, hasCompletedAllSections, featuresData]);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();

      const threshold = 0.3 * window.innerHeight;
      const isSectionDominant =
        rect.top <= threshold && rect.bottom >= window.innerHeight - threshold;

      // Reset when section is no longer dominant (scrolled away up or down)
      if (!isSectionDominant) {
        if (rect.top > window.innerHeight) {
          setHasCompletedAllSections(false);
          updateCurrentSection(0);
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasCompletedAllSections, currentSection]);

  return (
    <section
      ref={containerRef}
      className="py-6 md:py-10 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 overflow-hidden relative min-h-screen"
    >
      <BackgroundAnimations />

      <div className="container mx-auto px-4 relative z-10 h-full flex flex-col justify-center">
        <div className="text-center mb-8 md:mb-10">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
            {sectionTitle}
          </h2>
          <p className="text-base md:text-lg text-gray-700 max-w-2xl mx-auto leading-relaxed px-2">
            {sectionDescription}
          </p>

          {/* Progress indicator - simplified without animation */}
          <div className="mt-6 flex justify-center">
            <div className="bg-gray-200 rounded-full h-2 w-48 md:w-64">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${((currentSection + 1) / featuresData.length) * 100}%`,
                }}
              />
            </div>
          </div>
        </div>

        {featuresData && featuresData.length > 0 ? (
          <>
            <div className="flex-1 flex items-center justify-center">
              <FeatureDisplay currentSection={currentSection} />
            </div>

            <NavigationDots
              currentSection={currentSection}
              isScrollLocked={isScrollLocked}
              onSectionChange={updateCurrentSection}
              totalSections={featuresData.length}
            />

            <ScrollInstruction
              currentSection={currentSection}
              hasCompletedAllSections={hasCompletedAllSections}
              totalSections={featuresData.length}
            />
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-gray-500">{loadingText}</p>
          </div>
        )}

        <StatsSection />
      </div>
    </section>
  );
};

export default OverviewSection;
